import { isNil, union } from 'lodash';
import IAuditTrack, { AuditTracksDiff } from '../../../utilities/schemas/auditTrack';
import stringDiff from '../../../utilities/auditTracks/stringDiff';
import arrayDiff from '../../../utilities/auditTracks/arrayDiff';
import valueDiff from '../../../utilities/auditTracks/valueDiff';

export type AuditTrack<Resource> = IAuditTrack & {
  documentAfter: Resource;
}

type AuditTrackResourceChangeData<T> = {
  hasChanged: boolean;
  oldValue: T;
  newValue: T;
};

// Leaf (string/number/boolean/date) changes additionally expose the raw values
// so that reference, select and date diff fields can resolve and format them the
// same way as the resource edit views, instead of showing raw ids.
type AuditTrackLeafChangeData<T> = AuditTrackResourceChangeData<string> & {
  oldRawValue?: T;
  newRawValue?: T;
};

type ResourceData<Resource> = {
  [K in keyof Resource]: Exclude<Resource[K], undefined> extends Array<any>
    ? AuditTrackResourceChangeData<Resource[K]>
    // TODO: Change all date types in frontend to strings since we cannot get Dates from JSON
    // Then remove this check
    : Exclude<Resource[K], undefined> extends Date
      ? AuditTrackLeafChangeData<Resource[K]>
      : Exclude<Resource[K], undefined> extends object
        ? ResourceData<Exclude<Resource[K], undefined>>
        : AuditTrackLeafChangeData<Resource[K]>;
};

export type ExtendedAuditTrackDiff<Resource> = AuditTracksDiff & {
  resourceData: ResourceData<Resource>;
}

const isDateLikeString = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const time = Date.parse(value);
  return !Number.isNaN(time);
};

const getDocumentDiffs = <Resource>({
  oldDocument,
  newDocument,
}: {
  oldDocument?: Resource;
  newDocument: Resource;
}): Record<keyof Resource, any> => {
  const keys = union(
    Object.keys(oldDocument ?? {}),
    Object.keys(newDocument ?? {}),
  ) as Array<keyof Resource>;

  const results = keys.map((key) => {
    const oldValue = oldDocument ? oldDocument[key] : undefined;
    const newValue = newDocument[key];
    const rawValues = { oldRawValue: oldValue, newRawValue: newValue };
    if (typeof oldValue === 'string' || typeof newValue === 'string') {
      return [
        key,
        {
          ...stringDiff({
            oldValue: !isNil(oldValue) ? oldValue as string : '',
            newValue: !isNil(newValue) ? newValue as string : '',
          }),
          ...rawValues,
        },
      ];
    }

    if (typeof oldValue === 'number' || typeof newValue === 'number') {
      return [
        key,
        {
          ...valueDiff({
            oldValue: !isNil(oldValue) ? (oldValue as number).toString() : '',
            newValue: !isNil(newValue) ? (newValue as number).toString() : '',
          }),
          ...rawValues,
        },
      ];
    }

    if (typeof oldValue === 'boolean' || typeof newValue === 'boolean') {
      return [
        key,
        {
          ...valueDiff({
            oldValue: !isNil(oldValue) ? (oldValue as boolean).toString() : '',
            newValue: !isNil(newValue) ? (newValue as boolean).toString() : '',
          }),
          ...rawValues,
        },
      ];
    }

    if (isDateLikeString(oldValue) || isDateLikeString(newValue)) {
      return [
        key,
        {
          ...valueDiff({
            oldValue: !isNil(oldValue) ? String(oldValue) : '',
            newValue: !isNil(newValue) ? String(newValue) : '',
          }),
          ...rawValues,
        },
      ];
    }

    if (Array.isArray(oldValue) || Array.isArray(newValue)) {
      const oldArray = Array.isArray(oldValue) ? (oldValue as unknown[]) : [];
      const newArray = Array.isArray(newValue) ? (newValue as unknown[]) : [];

      return [
        key,
        arrayDiff({
          oldValue: oldArray,
          newValue: newArray,
        }),
      ];
    }

    return [
      key,
      getDocumentDiffs({
        oldDocument: oldValue as typeof oldValue,
        newDocument: newValue as typeof newValue,
      })];
  });

  return Object.fromEntries(results);
};

const auditTrackDiffGenerator = <Resource>({
  current,
  previous,
}: {
  current: AuditTrack<Resource>;
  previous?: AuditTrack<Resource>;
}) => {
  const resourceData: ResourceData<Resource> = getDocumentDiffs<Resource>({
    oldDocument: previous?.documentAfter,
    newDocument: current.documentAfter,
  });

  return {
    id: current.id,
    resourceData,
    authenticator: {
      id: current.authenticator.id,
      type: current.authenticator.type,
    },
    description: current.description,
    date: current.date,
  };
};

export default auditTrackDiffGenerator;
